import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Button, Modal, Form } from "react-bootstrap";
import { getOrganization, addSite, inviteUser, setUserStatus, updateOrganization } from "../../services/orgService";
import { errorToast, successToast } from "../Toasts/Toasts";

// CMS: one organization - its sites, its portal users, its settings. This
// is how MG onboards a society: create it from Zoho, add a site, invite the
// manager's phone.

const ROLES = { CUST_ADMIN: "Admin", CUST_MANAGER: "Manager", CUST_APPROVER: "Approver", CUST_ACCOUNTS: "Accounts" };

const OrganizationDetail = () => {
  const { id } = useParams();
  const [org, setOrg] = useState(null);
  const [siteModal, setSiteModal] = useState(false);
  const [userModal, setUserModal] = useState(false);
  const [site, setSite] = useState({ name: "", contactName: "", contactPhone: "", line1: "", city: "", pincode: "" });
  const [invite, setInvite] = useState({ name: "", phone: "", role: "CUST_ADMIN" });

  const load = useCallback(async () => {
    try { setOrg(await getOrganization(id)); } catch (e) { errorToast("Could not load"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const saveSite = async (e) => {
    e.preventDefault();
    try {
      const r = await addSite(id, { name: site.name, contactName: site.contactName, contactPhone: site.contactPhone, deliveryAddress: { line1: site.line1, city: site.city, pincode: site.pincode } });
      if (r?.success) { successToast("Site added"); setSiteModal(false); load(); } else errorToast(r?.message);
    } catch (err) { errorToast(err?.message); }
  };
  const sendInvite = async (e) => {
    e.preventDefault();
    try {
      const r = await inviteUser(id, invite);
      if (r?.success) { successToast("Invited"); setUserModal(false); setInvite({ name: "", phone: "", role: "CUST_ADMIN" }); load(); } else errorToast(r?.message);
    } catch (err) { errorToast(err?.message); }
  };
  const toggleUser = async (u) => {
    try {
      const r = await setUserStatus(id, u.id, u.status === "disabled" ? "active" : "disabled");
      if (r?.success) load(); else errorToast(r?.message);
    } catch (err) { errorToast(err?.message); }
  };
  const setSetting = async (key, value) => {
    try {
      const r = await updateOrganization(id, { settings: { [key]: value } });
      if (r?.success) load(); else errorToast(r?.message);
    } catch (err) { errorToast(err?.message); }
  };

  if (!org) return null;
  const s = org.settings || {};

  return (
    <div className="page-body px-0 py-lg-2 py-1 mt-0 mt-lg-3 ms-3">
      <div className="container-fluid p-0">
        <div className="row-title px-2 mb-2">
          <div className="small"><Link to="/organizations">Organizations</Link> / {org.name}</div>
          <h6 className="fw-bold mb-0">{org.name} <span className="badge bg-light text-dark">{org.type}</span> <span className="badge bg-light text-dark">{org.status}</span></h6>
          {org.zohoAccountId && <div className="small text-muted">Zoho Account {org.zohoAccountId}{org.billingAddress?.city ? ` · ${org.billingAddress.city}` : ""}</div>}
          {/* Which Books customer the invoices on this account come from, and
              whether anyone decided that. A name match can attach "Gaur City"
              to "Gaur City 1st Avenue", so a guess is labelled as one. */}
          {org.zohoBooksContactId && (
            <div className="small text-muted">
              Bills as Books customer {org.zohoBooksContactId}
              {org.booksContactSource === "crm_mapping" ? (
                <span className="badge bg-light text-dark border ms-1" title="from Account Customer Mappings in the CRM">mapped in the CRM</span>
              ) : (
                <span className="badge bg-warning text-dark ms-1" title="matched on the name by this app, not confirmed by anyone. Map it in the CRM to settle it.">matched by name</span>
              )}
            </div>
          )}
          {/* No link at all looked identical to a linked society here, so an
              unbilled account could sit unnoticed. Said plainly instead, with
              where the fix lives - it is a row in the CRM, not a field here. */}
          {!org.zohoBooksContactId && (
            <div className="small text-warning">
              Not linked to a Zoho Books customer — invoices and outstanding balances will not show.
              Add a row in the CRM under Account Customer Mappings and it is picked up on the next load.
            </div>
          )}
        </div>

        <div className="row g-3">
          <div className="col-lg-6">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="fw-bold mb-0">Sites</h6>
                  <Button size="sm" variant="outline-success" onClick={() => setSiteModal(true)}>Add site</Button>
                </div>
                {org.sites?.length ? org.sites.map((st) => (
                  <div key={st.id} className="border-bottom py-2">
                    <div className="fw-semibold">{st.name}</div>
                    <div className="small text-muted">{[st.deliveryAddress?.line1, st.deliveryAddress?.city, st.deliveryAddress?.pincode].filter(Boolean).join(", ")}</div>
                    {st.contactName && <div className="small text-muted">{st.contactName} {st.contactPhone && `· +${st.contactPhone}`}</div>}
                  </div>
                )) : <div className="text-muted small">No sites.</div>}
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="fw-bold mb-0">Portal users</h6>
                  <Button size="sm" variant="outline-success" onClick={() => setUserModal(true)}>Invite</Button>
                </div>
                {org.users?.length ? org.users.map((u) => (
                  <div key={u.id} className="border-bottom py-2 d-flex justify-content-between align-items-center">
                    <div>
                      <div className="fw-semibold">{u.name || "—"} <span className="badge bg-light text-dark">{ROLES[u.role] || u.role}</span> <span className={`badge ${u.status === "active" ? "bg-success" : u.status === "invited" ? "bg-warning text-dark" : "bg-secondary"}`}>{u.status}</span></div>
                      <div className="small text-muted">+{u.phone}{u.lastLoginAt ? ` · last login ${new Date(u.lastLoginAt).toLocaleDateString("en-IN")}` : ""}</div>
                    </div>
                    <Button size="sm" variant="outline-secondary" onClick={() => toggleUser(u)}>{u.status === "disabled" ? "Enable" : "Disable"}</Button>
                  </div>
                )) : <div className="text-muted small">No users invited yet.</div>}
              </div>
            </div>

            <div className="card mt-3">
              <div className="card-body">
                <h6 className="fw-bold">Settings</h6>
                <Form.Check type="switch" id="po" label="Purchase order required before an order is confirmed" checked={!!s.poRequired} onChange={(e) => setSetting("poRequired", e.target.checked)} />
                <Form.Group className="mt-2">
                  <Form.Label className="small mb-1">Who may approve quotations</Form.Label>
                  <Form.Select size="sm" value={s.approvalRule || "approver"} onChange={(e) => setSetting("approvalRule", e.target.value)}>
                    <option value="approver">Approver or Admin roles only</option>
                    <option value="manager">Managers too</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={siteModal} onHide={() => setSiteModal(false)}>
        <Form onSubmit={saveSite}>
          <Modal.Header closeButton><Modal.Title>Add a site</Modal.Title></Modal.Header>
          <Modal.Body>
            {[["name", "Site name", true], ["contactName", "Contact person"], ["contactPhone", "Contact mobile"], ["line1", "Address"], ["city", "City"], ["pincode", "Pincode"]].map(([k, label, req]) => (
              <Form.Group className="mb-2" key={k}><Form.Label>{label}</Form.Label><Form.Control required={!!req} value={site[k]} onChange={(e) => setSite({ ...site, [k]: e.target.value })} /></Form.Group>
            ))}
          </Modal.Body>
          <Modal.Footer><Button type="submit" variant="success">Save</Button></Modal.Footer>
        </Form>
      </Modal>

      <Modal show={userModal} onHide={() => setUserModal(false)}>
        <Form onSubmit={sendInvite}>
          <Modal.Header closeButton><Modal.Title>Invite a portal user</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-2"><Form.Label>Name</Form.Label><Form.Control value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} /></Form.Group>
            <Form.Group className="mb-2"><Form.Label>Mobile (WhatsApp)</Form.Label><Form.Control required inputMode="numeric" value={invite.phone} onChange={(e) => setInvite({ ...invite, phone: e.target.value })} /></Form.Group>
            <Form.Group className="mb-2"><Form.Label>Role</Form.Label>
              <Form.Select value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })}>
                {Object.entries(ROLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </Form.Select>
            </Form.Group>
            <div className="small text-muted">They sign in at the portal with this number and a WhatsApp code. The first user should be an Admin.</div>
          </Modal.Body>
          <Modal.Footer><Button type="submit" variant="success">Send invitation</Button></Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default OrganizationDetail;
